import {TypeAliceIqEnvConfig} from "../../utils/env.types";

export type TypeAliceIqStart = {
    wlCode?: string;
    walletId?: string;
    gameName?: string;
};

type TypeAliceIqResult = {
    superStrong?: {
        unique?: {
            id?: string;
        }
    }
};

export type TypeAliceLibrary = {
    analyticc(data: Object): Promise<TypeAliceIqResult>
};

export type TypeAliceIqVisitor = {
    visitorId?: string;
};

export class AliceIQService {
    public visitor: TypeAliceIqVisitor | undefined;

    private version = 2;

    /**
     * @doc "docs/aliceiq-fingerprint-integration-ru.md"
     * Team - DG: counter_8fge6oj7urcpr4sn
     */
    private counterId = "counter_8fge6oj7urcpr4sn"

    private _loadingScript = false;
    private _initted = false;
    private _initData: TypeAliceIqStart | undefined;
    private _subscribers: Array<(config: TypeAliceIqVisitor) => void> = [];

    constructor(private config: TypeAliceIqEnvConfig) {}

    get fingerPrint(): TypeAliceLibrary | undefined {
        return window.TulgeyIO ?? window.AliceIQ;
    }

    load(attempt = 1): void {
        if (!this.config || !this.config.url) {
            console.warn('[BASH] AliceIQService not configured.');
            return;
        }

        if (this.config.version !== this.version) {
            console.warn(`[BASH] AliceIQService version is not correct! Expected version: "${this.version}"`);
            return;
        }

        // no logging for local develop
        if (/^https?:\/\/localhost(?::\d+)?$/.test(window.location.origin)) {
            console.warn('[BASH] AliceIQService suspended for local developing.');
            return;
        }

        if (this.fingerPrint) {
            console.log('AliceIQ CDN already loaded');
            if (this._initData) {
                this._init();
            }
            return;
        }

        if (this._loadingScript) return;
        this._loadingScript = true;

        const MAX_ATTEMPTS = 3;
        const RETRY_DELAY = 1000;

        try {
            const script = document.createElement('script');
            script.src = this.config.url;
            script.async = true;

            script.onload = () => {
                this._loadingScript = false;
                console.log('AliceIQ CDN loaded successfully');
                if (this._initData) this._init();
            };

            script.onerror = () => {
                script.remove();
                this._loadingScript = false;

                if (attempt < MAX_ATTEMPTS) {
                    console.warn(`AliceIQ CDN load failed. Retry ${attempt}/${MAX_ATTEMPTS}...`);
                    setTimeout(() => this.load(attempt + 1), RETRY_DELAY);
                } else {
                    console.warn("Failed to load AliceIQ CDN after 3 attempts");
                }
            };

            document.body.appendChild(script);
        } catch (err: unknown) {
            this._loadingScript = false;
            console.warn('AliceIQ CDN load threw an unexpected error', err);
        }
    }


    setConfig(initData: TypeAliceIqStart): void {
        this._initData = initData;
        this._init();
    }

    subscribe(fn: (config: TypeAliceIqVisitor) => void): () => void {
        if (this._initted && this.visitor) {
            fn(this.visitor);
            return () => {};
        }

        this._subscribers.push(fn);

        return () => {
            const i = this._subscribers.indexOf(fn);
            if (i >= 0) this._subscribers.splice(i, 1);
        };
    }

    private async _init(): Promise<void> {
        if (!this.fingerPrint || typeof this.fingerPrint?.analyticc !== 'function' || !this._initData || this._initted) {
            return;
        }

        this._initted = true;

        const { wlCode, walletId, gameName } = this._initData;

        try {
            const result = await this.fingerPrint.analyticc({
                context: {
                    scope:     wlCode,      // WL-code (alternative - partner)
                    entity:    walletId     // hash_wlid || userId, (unique user identify)
                },
                contentId: gameName,
                counterId: this.counterId,
            });

            this.visitor = {
                visitorId: result.superStrong?.unique?.id,
            };

            this._emit();
        } catch (err: unknown) {
            this._initted = false;
        }
    }

    private _emit(): void {
        if (!this.visitor) {
            return;
        }

        let fn: ((v: TypeAliceIqVisitor) => void) | undefined;
        while ((fn = this._subscribers.pop())) {
            fn(this.visitor);
        }
    }
}
